import { inject, Injectable } from '@angular/core';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';
import { log } from '../utils/logger';

const logger = log.getLogger('lead-note.service');

export interface LeadNoteDto {
  id: string;
  content: string;
  agentId: string;
  createdAt: string;
  updatedAt?: string;
  canDelete: boolean;
}

export interface LeadNotesListDto {
  notes: LeadNoteDto[];
}

@Injectable({
  providedIn: 'root',
})
export class LeadNoteService {
  private readonly api = inject(EnrollhereApiService);
  private readonly listRequests = new Map<
    string,
    { expiresAt: number; promise: Promise<LeadNotesListDto> }
  >();

  listNotes(agencyId: string, leadId: string): Promise<LeadNotesListDto> {
    const key = `${agencyId}:${leadId}`;
    const cached = this.listRequests.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.promise;
    }
    const entry = {
      expiresAt: Number.POSITIVE_INFINITY,
      promise: this.api
        .get<LeadNotesListDto>(`dialer/agency/${agencyId}/lead/${leadId}/notes`)
        .catch(error => {
          logger.error('Error listing lead notes', error);
          return { notes: [] };
        }),
    };
    entry.promise = entry.promise.then(response => {
      if (this.listRequests.get(key) === entry) {
        entry.expiresAt = Date.now() + 30_000;
      }
      return response;
    });
    this.listRequests.set(key, entry);
    return entry.promise;
  }

  async createNote(
    agencyId: string,
    leadId: string,
    content: string
  ): Promise<LeadNoteDto | null> {
    try {
      const note = await this.api.post<LeadNoteDto>(
        `dialer/agency/${agencyId}/lead/${leadId}/note`,
        { content }
      );
      this.listRequests.delete(`${agencyId}:${leadId}`);
      return note;
    } catch (error) {
      logger.error('Error creating lead note', error);
      return null;
    }
  }

  async updateNote(
    agencyId: string,
    leadId: string,
    noteId: string,
    content: string
  ): Promise<LeadNoteDto | null> {
    try {
      const note = await this.api.put<LeadNoteDto>(
        `dialer/agency/${agencyId}/lead/${leadId}/note/${noteId}`,
        { content }
      );
      this.listRequests.delete(`${agencyId}:${leadId}`);
      return note;
    } catch (error) {
      logger.error('Error updating lead note', error);
      return null;
    }
  }

  async deleteNote(
    agencyId: string,
    leadId: string,
    noteId: string
  ): Promise<boolean> {
    try {
      await this.api.delete(
        `dialer/agency/${agencyId}/lead/${leadId}/note/${noteId}`
      );
      this.listRequests.delete(`${agencyId}:${leadId}`);
      return true;
    } catch (error) {
      logger.error('Error deleting lead note', error);
      return false;
    }
  }
}
